/**
 * Goals Service
 *
 * Service for interacting with the Goals NGXS state.
 * Provides methods for loading, filtering, and managing goals.
 */

import { Injectable } from '@angular/core';
import { Store, Select } from '@ngxs/store';
import { Observable } from 'rxjs';
import {
  InitializeGoals,
  InitializeGoalsSuccess,
  InitializeGoalsFailure,
  LoadNextBatch,
  LoadNextBatchSuccess,
  LoadNextBatchFailure,
  SetGoalsFilters,
  SetGoalsSort,
  AddGoal,
  AddGoalSuccess,
  AddGoalFailure,
  UpdateGoal,
  UpdateGoalSuccess,
  UpdateGoalFailure,
  DeleteGoal,
  DeleteGoalSuccess,
  DeleteGoalFailure,
  UpdateGoalsFromListener,
  ClearGoals,
  ClearGoalsError,
} from './goals.actions';
import { GoalsState, GoalsStateModel } from './goals.state';
import { Goal } from 'src/app/models/schema';
import { FirestoreService } from 'src/app/services/firestore/firestore';
import { ObjectivesService } from 'src/app/rxjs-store/admin/objectives';

@Injectable({
  providedIn: 'root',
})
export class GoalsService {
  private readonly PAGE_SIZE = 25;

  /**
   * Current partner ID for determining subcollection path
   */
  private currentPartnerId: string | null = null;

  /**
   * Last document snapshot for pagination
   */
  private lastDocument: any = null;

  /**
   * Observable of the entire goals state
   */
  @Select(GoalsState.getState) state$!: Observable<GoalsStateModel>;

  /**
   * Observable of all goals
   */
  @Select(GoalsState.goals) goals$!: Observable<Goal[]>;

  /**
   * Observable of goals sorted by date
   */
  @Select(GoalsState.goalsSorted) goalsSorted$!: Observable<Goal[]>;

  /**
   * Observable of loading state
   */
  @Select(GoalsState.loading) loading$!: Observable<boolean>;

  /**
   * Observable of initialized state
   */
  @Select(GoalsState.initialized) initialized$!: Observable<boolean>;

  /**
   * Observable of error state
   */
  @Select(GoalsState.error) error$!: Observable<string | null>;

  /**
   * Observable of hasMore pagination state
   */
  @Select(GoalsState.hasMore) hasMore$!: Observable<boolean>;

  /**
   * Observable of current filters
   */
  @Select(GoalsState.filters) filters$!: Observable<any>;

  /**
   * Observable of current sort
   */
  @Select(GoalsState.sort) sort$!: Observable<{ field: string; direction: 'asc' | 'desc' }>;

  constructor(
    private store: Store,
    private firestoreService: FirestoreService,
    private objectivesService: ObjectivesService
  ) {}

  /**
   * Get current goals snapshot
   */
  get goals(): Goal[] {
    return this.store.selectSnapshot(GoalsState.goals);
  }

  /**
   * Get current loading state snapshot
   */
  get loading(): boolean {
    return this.store.selectSnapshot(GoalsState.loading);
  }

  /**
   * Get current initialized state snapshot
   */
  get initialized(): boolean {
    return this.store.selectSnapshot(GoalsState.initialized);
  }

  /**
   * Get current hasMore state snapshot
   */
  get hasMore(): boolean {
    return this.store.selectSnapshot(GoalsState.hasMore);
  }

  /**
   * Initialize goals - load first batch
   * @param partnerId - The partner ID to load goals for
   */
  async initialize(partnerId: string): Promise<void> {
    try {
      this.currentPartnerId = partnerId;
      this.lastDocument = null; // Reset pagination cursor

      await this.store.dispatch(new InitializeGoals(partnerId)).toPromise();

      const state = this.store.selectSnapshot(GoalsState.getState);
      const { filters, sortField, sortDirection } = state;

      // Build query path
      const collectionPath = `partners/${partnerId}/goals`;

      // Build query options
      const queryOptions: any = {
        orderBy: [{ field: sortField, direction: sortDirection }],
        limit: this.PAGE_SIZE + 1, // Request one extra to check if more exist
      };

      // Add filters if present
      if (filters && Object.keys(filters).length > 0) {
        queryOptions.where = [];
        
        // Handle date range filters
        if (filters.startDate) {
          queryOptions.where.push({ field: 'createdAt', operator: '>=', value: filters.startDate });
        }
        if (filters.endDate) {
          queryOptions.where.push({ field: 'createdAt', operator: '<=', value: filters.endDate });
        }

        // Handle skill area filters
        if (filters.majorSkillAreaId) {
          queryOptions.where.push({ field: 'majorSkillAreaId', operator: '==', value: filters.majorSkillAreaId });
        }
        if (filters.subsetSkillAreaId) {
          queryOptions.where.push({ field: 'subsetSkillAreaId', operator: '==', value: filters.subsetSkillAreaId });
        }

        // Handle other filters (exact match)
        for (const [field, value] of Object.entries(filters)) {
          if (
            field !== 'startDate' && 
            field !== 'endDate' && 
            field !== 'majorSkillAreaId' && 
            field !== 'subsetSkillAreaId' && 
            value !== null && 
            value !== undefined && 
            value !== ''
          ) {
            queryOptions.where.push({ field, operator: '==', value });
          }
        }
      }

      // Fetch first batch
      const results = await this.firestoreService.getCollectionAsync<Goal>(
        collectionPath,
        queryOptions
      );

      const hasMore = results.length > this.PAGE_SIZE;
      const goals = hasMore ? results.slice(0, this.PAGE_SIZE) : results;

      // Store last document for pagination
      if (goals.length > 0) {
        const lastGoal = goals[goals.length - 1] as any;
        this.lastDocument = lastGoal[sortField];
      }

      await this.store.dispatch(
        new InitializeGoalsSuccess(goals, hasMore)
      ).toPromise();
    } catch (error: any) {
      console.error('[GoalsService] Error initializing:', error);
      await this.store.dispatch(
        new InitializeGoalsFailure(error.message || 'Failed to load goals')
      ).toPromise();
      throw error;
    }
  }

  /**
   * Load next batch of goals
   */
  async loadNextBatch(): Promise<void> {
    const state = this.store.selectSnapshot(GoalsState.getState);
    
    if (!state.hasMore || state.loading || !this.lastDocument) {
      return;
    }

    try {
      await this.store.dispatch(new LoadNextBatch()).toPromise();

      const { partnerId, filters, sortField, sortDirection } = state;

      if (!partnerId) {
        throw new Error('No partner ID set');
      }

      // Build query path
      const collectionPath = `partners/${partnerId}/goals`;

      // Build query options with pagination cursor
      const queryOptions: any = {
        orderBy: [{ field: sortField, direction: sortDirection }],
        limit: this.PAGE_SIZE + 1, // Request one extra to check if more exist
        startAfter: this.lastDocument,
      };

      // Add filters if present
      if (filters && Object.keys(filters).length > 0) {
        queryOptions.where = [];
        
        // Handle date range filters
        if (filters.startDate) {
          queryOptions.where.push({ field: 'createdAt', operator: '>=', value: filters.startDate });
        }
        if (filters.endDate) {
          queryOptions.where.push({ field: 'createdAt', operator: '<=', value: filters.endDate });
        }

        // Handle skill area filters
        if (filters.majorSkillAreaId) {
          queryOptions.where.push({ field: 'majorSkillAreaId', operator: '==', value: filters.majorSkillAreaId });
        }
        if (filters.subsetSkillAreaId) {
          queryOptions.where.push({ field: 'subsetSkillAreaId', operator: '==', value: filters.subsetSkillAreaId });
        }

        // Handle other filters (exact match)
        for (const [field, value] of Object.entries(filters)) {
          if (
            field !== 'startDate' && 
            field !== 'endDate' && 
            field !== 'majorSkillAreaId' && 
            field !== 'subsetSkillAreaId' && 
            value !== null && 
            value !== undefined && 
            value !== ''
          ) {
            queryOptions.where.push({ field, operator: '==', value });
          }
        }
      }

      // Fetch next batch
      const results = await this.firestoreService.getCollectionAsync<Goal>(
        collectionPath,
        queryOptions
      );

      const hasMore = results.length > this.PAGE_SIZE;
      const newGoals = hasMore ? results.slice(0, this.PAGE_SIZE) : results;

      // Update last document for next pagination
      if (newGoals.length > 0) {
        const lastGoal = newGoals[newGoals.length - 1] as any;
        this.lastDocument = lastGoal[sortField];
      }

      await this.store.dispatch(
        new LoadNextBatchSuccess(newGoals, hasMore)
      ).toPromise();
    } catch (error: any) {
      console.error('[GoalsService] Error loading next batch:', error);
      await this.store.dispatch(
        new LoadNextBatchFailure(error.message || 'Failed to load more goals')
      ).toPromise();
      throw error;
    }
  }

  /**
   * Set filter criteria and reload
   * @param filters - Filter object with field names and values
   */
  async setFilters(filters: any): Promise<void> {
    await this.store.dispatch(new SetGoalsFilters(filters)).toPromise();
    
    // Reload with new filters
    const partnerId = this.store.selectSnapshot(GoalsState.partnerId);
    if (partnerId) {
      await this.initialize(partnerId);
    }
  }

  /**
   * Set sort criteria and reload
   * @param sortField - Field to sort by
   * @param sortDirection - Sort direction
   */
  async setSort(sortField: string, sortDirection: 'asc' | 'desc'): Promise<void> {
    await this.store.dispatch(new SetGoalsSort(sortField, sortDirection)).toPromise();
    
    // Reload with new sort
    const partnerId = this.store.selectSnapshot(GoalsState.partnerId);
    if (partnerId) {
      await this.initialize(partnerId);
    }
  }

  /**
   * Add a new goal
   * @param goal - The goal to add
   * @param partnerId - The partner ID to add the goal to
   */
  async addGoal(goal: Partial<Goal>, partnerId: string): Promise<Goal> {
    try {
      await this.store.dispatch(new AddGoal(goal as Goal)).toPromise();

      // Extract objectives before saving (they go in separate collection)
      const objectives = goal.objectives || [];
      
      // Remove objectives from goal data (don't save to goals collection)
      const goalDataWithoutObjectives = { ...goal };
      delete goalDataWithoutObjectives.objectives;

      // Build the document path
      const collectionPath = `partners/${partnerId}/goals`;

      // Add timestamps
      const goalData = {
        ...goalDataWithoutObjectives,
        createdAt: new Date(),
        updatedAt: new Date(),
      };

      // Save goal to Firestore
      const docRef = await this.firestoreService.addDocumentAsync(collectionPath, goalData);
      
      // Update objectives with the Firestore-generated goal ID
      const objectivesWithGoalId = objectives.map((obj) => ({
        ...obj,
        goalId: docRef.id,
      }));

      // Save objectives to separate collection
      if (objectivesWithGoalId.length > 0) {
        await this.objectivesService.addObjectivesBatch(objectivesWithGoalId, partnerId);
      }

      const newGoal = { 
        ...goalData, 
        id: docRef.id,
        createdAt: typeof goalData.createdAt === 'number' ? goalData.createdAt : (goalData.createdAt as any).getTime(),
        updatedAt: typeof goalData.updatedAt === 'number' ? goalData.updatedAt : (goalData.updatedAt as any).getTime(),
      } as Goal;

      await this.store.dispatch(new AddGoalSuccess(newGoal)).toPromise();
      return newGoal;
    } catch (error: any) {
      console.error('[GoalsService] Error adding goal:', error);
      await this.store.dispatch(
        new AddGoalFailure(error.message || 'Failed to add goal')
      ).toPromise();
      throw error;
    }
  }

  /**
   * Update an existing goal
   * @param goal - The goal to update
   * @param partnerId - The partner ID
   */
  async updateGoal(goal: Goal, partnerId: string): Promise<void> {
    try {
      await this.store.dispatch(new UpdateGoal(goal)).toPromise();

      // Build the document path
      const docPath = `partners/${partnerId}/goals/${goal.id}`;

      // Update timestamp
      const goalData = {
        ...goal,
        updatedAt: new Date(),
      };

      // Update in Firestore
      await this.firestoreService.updateDocumentAsync(docPath, goalData);

      const updatedGoal = {
        ...goalData,
        createdAt: typeof goalData.createdAt === 'number' ? goalData.createdAt : (goalData.createdAt as any).getTime(),
        updatedAt: typeof goalData.updatedAt === 'number' ? goalData.updatedAt : (goalData.updatedAt as any).getTime(),
      } as Goal;

      await this.store.dispatch(new UpdateGoalSuccess(updatedGoal)).toPromise();
    } catch (error: any) {
      console.error('[GoalsService] Error updating goal:', error);
      await this.store.dispatch(
        new UpdateGoalFailure(error.message || 'Failed to update goal')
      ).toPromise();
      throw error;
    }
  }

  /**
   * Delete a goal
   * @param goalId - The ID of the goal to delete
   * @param partnerId - The partner ID
   */
  async deleteGoal(goalId: string, partnerId: string): Promise<void> {
    try {
      await this.store.dispatch(new DeleteGoal(goalId)).toPromise();

      // Build the document path
      const docPath = `partners/${partnerId}/goals/${goalId}`;

      // Delete goal from Firestore
      await this.firestoreService.deleteDocumentAsync(docPath);

      // CASCADE DELETE: Delete all objectives for this goal
      await this.objectivesService.deleteObjectivesByGoal(goalId, partnerId);

      await this.store.dispatch(new DeleteGoalSuccess(goalId)).toPromise();
    } catch (error: any) {
      console.error('[GoalsService] Error deleting goal:', error);
      await this.store.dispatch(
        new DeleteGoalFailure(error.message || 'Failed to delete goal')
      ).toPromise();
      throw error;
    }
  }

  /**
   * Clear all goals data
   */
  async clear(): Promise<void> {
    await this.store.dispatch(new ClearGoals()).toPromise();
  }

  /**
   * Clear error state
   */
  async clearError(): Promise<void> {
    await this.store.dispatch(new ClearGoalsError()).toPromise();
  }
}
